// 파일 별도 창의 **세션 창 쪽**(설계서 §9.4 "파일 창", 2026-09-29) — 창을 열고 닫고, 상태를 보내고, 사람의 동작을 받아 처리한다.
// 채팅 별도 창(`chat/useChatWindow.ts`)과 같은 틀이다.
//
// 규칙:
//   - 창이 필요한 때 = 파일 화면을 쓸 수 있고 · 열려 있을 때. 그 밖에는 닫는다.
//   - 창을 열지 못하면(브라우저 팝업 차단 등) **원격 화면 안 패널로 대신** 보이고 이유를 알린다 — 파일 화면이 사라지면 안 된다.
//     패널을 닫으면 다음에는 다시 창을 시도한다.
//   - 사람이 창을 X 로 닫으면(`bye`) 파일 화면을 "닫힘" 으로 돌린다. 🔴 우리가 닫은 경우의 `bye` 는 무시한다
//     (닫기 순서가 엇갈려 방금 다시 연 화면을 닫지 않게).
//   - 세션이 끝나면(화면이 사라지면) `end` 를 보내고 닫는다. 앱은 세션 창이 사라질 때 Rust 가 한 번 더 닫는다.

import { useCallback, useEffect, useRef, useState } from 'react';

import { openPopup, type WindowLink } from '../popup/windowLink';
import {
  FILE_WINDOW_SPEC,
  isWinToHost,
  newFileWindowKey,
  runPanelOp,
  type FileViewState,
  type HostToWin,
  type WinToHost,
} from './fileWindowProtocol';
import type { FilePanel } from './useFilePanel';

/** 창을 열지 못했을 때의 안내(원격 화면 안 패널로 대신 보인 뒤 패널 위에 보인다). */
export const FILE_POPUP_BLOCKED_TEXT = '파일 창을 따로 띄우지 못해 화면 안에 열었습니다(브라우저가 팝업을 막았을 수 있습니다).';

type HostLink = WindowLink<WinToHost, HostToWin>;

export interface FileWindowControl {
  /** 별도 창이 떠 있는가. */
  isOpen: boolean;
  /** 창을 앞으로 가져온다(도구막대 [🗂 파일]을 다시 눌렀을 때). */
  focus(): void;
  /** 창을 못 띄워 원격 화면 안 패널로 보이는 중인가. */
  inStage: boolean;
  /** 그때의 안내. 없으면 undefined. */
  fallback?: string;
  clearFallback(): void;
}

/**
 * @param panel 세션 창의 파일 패널(진실원)
 * @param title 창 제목(기기 이름)
 */
export function useFileWindow(panel: FilePanel, title: string): FileWindowControl {
  const [inStage, setInStage] = useState(false);
  const [fallback, setFallback] = useState<string | undefined>(undefined);
  const want = panel.available && panel.open && !inStage;
  const keyRef = useRef<string>(newFileWindowKey());
  const linkRef = useRef<HostLink | null>(null);
  const unsubRef = useRef<(() => void) | null>(null);
  const openingRef = useRef(false);
  /** 우리가 닫는 중 — 그 뒤에 오는 `bye` 는 사람이 닫은 것이 아니다. */
  const closingByUsRef = useRef(false);
  const [isOpen, setIsOpen] = useState(false);

  // 처리기(한 번 등록)가 최신 값을 보게 한다.
  const panelRef = useRef(panel);
  panelRef.current = panel;
  const wantRef = useRef(want);
  wantRef.current = want;

  // 창이 그릴 상태. 🔴 알림의 버튼(`action` — 함수)은 창으로 보낼 수 없다 → 글자만(버튼 달린 알림은 도구 막대에 뜬다).
  const view: FileViewState = {
    title,
    local: panel.local,
    remote: panel.remote,
    canPickLocal: panel.canPickLocal,
    canSendPicked: panel.canSendPicked,
    busy: panel.busy,
    transfer: panel.transfer,
    notice: panel.notice ? { kind: panel.notice.kind, text: panel.notice.text } : null,
  };
  const viewRef = useRef(view);
  viewRef.current = view;

  const post = useCallback((msg: HostToWin) => linkRef.current?.post(msg), []);
  const pushState = useCallback(() => post({ t: 'state', s: viewRef.current }), [post]);

  const detach = useCallback(() => {
    unsubRef.current?.();
    unsubRef.current = null;
    linkRef.current = null;
    setIsOpen(false);
  }, []);

  const handle = useCallback(
    (msg: WinToHost) => {
      const p = panelRef.current;
      switch (msg.t) {
        case 'hello':
          pushState();
          break;
        case 'op':
          runPanelOp(p, msg.o);
          break;
        case 'localDir':
          p.openLocalHandle(msg.handle);
          break;
        case 'sendPicked':
          p.sendPicked(msg.picked, msg.folder);
          break;
        case 'cancel':
          p.cancel();
          break;
        case 'dismissNotice':
          p.dismissNotice();
          break;
        case 'close':
          p.setOpen(false);
          break;
        case 'bye':
          detach();
          if (closingByUsRef.current) {
            closingByUsRef.current = false;
          } else {
            p.setOpen(false); // 사람이 창을 X 로 닫았다 = 파일 화면을 닫았다
          }
          break;
      }
    },
    [detach, pushState],
  );

  // 열기·닫기.
  useEffect(() => {
    if (want && !linkRef.current && !openingRef.current) {
      openingRef.current = true;
      void openPopup<WinToHost, HostToWin>(FILE_WINDOW_SPEC, keyRef.current, title, isWinToHost).then((link) => {
        openingRef.current = false;
        if (!link) {
          setInStage(true);
          setFallback(FILE_POPUP_BLOCKED_TEXT);
          return;
        }
        linkRef.current = link;
        unsubRef.current = link.onMessage(handle);
        setIsOpen(true);
        setFallback(undefined);
        // 열리는 사이에 파일 화면이 닫혔으면 곧바로 닫는다.
        if (!wantRef.current) {
          closingByUsRef.current = true;
          link.close();
        }
        // 창이 이미 떠 있던 경우(앞으로 가져오기만 했다)에는 hello 가 다시 오지 않는다 → 한 번 밀어 준다.
        pushState();
      });
    } else if (!want && linkRef.current) {
      closingByUsRef.current = true;
      // 앱: Rust 가 창 소멸 때 bye 를 보낸다 → 처리기가 detach. 브라우저: 감시가 bye 를 만든다.
      linkRef.current.close();
    }
    // 🔴 `isOpen` 도 본다 — 닫자마자 다시 열면(bye 가 오기 전, 브라우저 감시 주기 ≤1초) 그때는 옛 창이 아직 붙어 있어
    //    열지 못한다. bye 로 떨어져 나가는(`isOpen` false) 순간 다시 봐야 새 창이 뜬다(2026-09-29 실세션 시험에서 발견).
  }, [want, isOpen, title, handle, pushState]);

  // 화면 안 패널로 대신 보이던 중에 파일 화면을 닫았다 → 다음에는 다시 창을 시도한다.
  useEffect(() => {
    if (!panel.open && inStage) {
      setInStage(false);
      setFallback(undefined);
    }
  }, [panel.open, inStage]);

  // 세션 화면이 사라지면 창도 닫는다.
  useEffect(
    () => () => {
      const link = linkRef.current;
      if (link) {
        link.post({ t: 'end' });
        closingByUsRef.current = true;
        link.close();
      }
      unsubRef.current?.();
    },
    [],
  );

  // 상태 보내기 — 그리는 데 필요한 값이 바뀔 때마다(같은 값이면 보내지 않는다).
  const viewKey = JSON.stringify(view);
  useEffect(() => {
    if (isOpen) {
      pushState();
    }
  }, [isOpen, viewKey, pushState]);

  return {
    isOpen,
    focus: useCallback(() => linkRef.current?.focus(), []),
    inStage,
    fallback,
    clearFallback: useCallback(() => setFallback(undefined), []),
  };
}
